import React from 'react';
import { Alert, Row, Table, Typography } from 'antd';
import SealApprovalAttachmentsView from '../Admin/SealApprovalAttachmentsView';
import {
  PrepaymentBasicInfoReadonly,
  PrepaymentPayeeReadonly,
  PrepaymentLinesReadonly,
  findOrgDisplayName
} from './PrepaymentReadonlyInfo';
import {
  FINANCE_READONLY_GUTTER,
  FinanceReadonlyItem,
  formatFinanceAmount,
  buildWriteoffInvoiceLineRows,
  createWriteoffInvoiceTableColumns
} from './financeReadonlyUtils';
import './ExpenseClaimForm.css';

const { Text } = Typography;

function buildAppendPreviewRows(lines = []) {
  return (lines || []).map((line, index) => ({
    key: String(index),
    lineNo: index + 1,
    expense_detail: line.expense_detail || '—',
    invoice_no: line.invoice_no || '—',
    invoice_amount: line.invoice_amount,
    actual_amount: line.actual_amount
  }));
}

export default function PrepaymentWriteoffReadonlyView({ formData = {}, organizations = [] }) {
  const prepayForm = formData.prepayment_snapshot || {};
  const prepaidTotal = Number(prepayForm.prepay_total || 0);
  const remainingBefore = Number(formData.remaining_before || 0);
  const writtenOffBefore = Math.max(0, prepaidTotal - remainingBefore);
  const prepaymentMeta = {
    prepaid_amount: prepaidTotal,
    written_off_amount: writtenOffBefore,
    remaining_amount: remainingBefore
  };

  const invoiceLines = buildWriteoffInvoiceLineRows(formData.invoice_lines);
  const invoiceTotal = Number(formData.invoice_total || 0);
  const resolution = formData.resolution;
  const resolutionLabel = formData.resolution_label || resolution || '—';
  const allocations = formData.writeoff_allocations || [];
  const appendPreview = formData.append_claim_preview;
  const refundFiles = formData.refund_attachments || [];

  const invoiceColumns = createWriteoffInvoiceTableColumns();

  const allocationColumns = [
    { title: '序号', dataIndex: 'lineNo', width: 56 },
    { title: '费用归属部门', dataIndex: 'org_name', width: 140, ellipsis: true },
    {
      title: '核销金额',
      dataIndex: 'amount',
      width: 110,
      align: 'right',
      render: (v) => formatFinanceAmount(v)
    }
  ];

  const appendPreviewColumns = [
    { title: '序号', dataIndex: 'lineNo', width: 56 },
    { title: '费用明细', dataIndex: 'expense_detail', width: 100, ellipsis: true },
    { title: '发票号', dataIndex: 'invoice_no', width: 120, ellipsis: true },
    {
      title: '发票金额',
      dataIndex: 'invoice_amount',
      width: 96,
      align: 'right',
      render: (v) => formatFinanceAmount(v)
    },
    {
      title: '实付金额',
      dataIndex: 'actual_amount',
      width: 96,
      align: 'right',
      render: (v) => formatFinanceAmount(v)
    }
  ];

  let resolutionAlert = null;
  if (resolution === 'partial_continue' && remainingBefore > invoiceTotal) {
    resolutionAlert = (
      <Alert
        type="warning"
        showIcon
        message={`发票总额 ${formatFinanceAmount(invoiceTotal)} 小于剩余应核销 ${formatFinanceAmount(remainingBefore)}，差额 ${formatFinanceAmount(remainingBefore - invoiceTotal)}`}
        style={{ marginBottom: 12 }}
      />
    );
  } else if (['supplier_over', 'append_claim'].includes(resolution) && invoiceTotal > remainingBefore) {
    resolutionAlert = (
      <Alert
        type="warning"
        showIcon
        message={`发票总额 ${formatFinanceAmount(invoiceTotal)} 超过剩余应核销 ${formatFinanceAmount(remainingBefore)}，超额 ${formatFinanceAmount(invoiceTotal - remainingBefore)}`}
        style={{ marginBottom: 12 }}
      />
    );
  }

  return (
    <div className="process-form-finance-grouped">
      <PrepaymentBasicInfoReadonly formData={prepayForm} organizations={organizations} />
      <PrepaymentPayeeReadonly formData={prepayForm} />
      <PrepaymentLinesReadonly
        prepayment={prepaymentMeta}
        formData={prepayForm}
        organizations={organizations}
        variant="writeoff"
      />

      <section className="expense-claim-section">
        <div className="expense-claim-section-title">核销发票明细</div>
        <Table
          rowKey="key"
          size="small"
          columns={invoiceColumns}
          dataSource={invoiceLines}
          pagination={false}
          tableLayout="fixed"
          scroll={{ x: 620 }}
          locale={{ emptyText: '暂无核销发票' }}
        />
        <div style={{ marginTop: 8, color: '#666' }}>
          发票合计：{formatFinanceAmount(invoiceTotal)}
          {Math.abs(invoiceTotal - remainingBefore) < 0.009 && invoiceTotal > 0 && (
            <span style={{ marginLeft: 8, color: '#52c41a' }}>（全额核销）</span>
          )}
        </div>
      </section>

      <section className="expense-claim-section">
        <div className="expense-claim-section-title">核销结论</div>
        {resolutionAlert}
        <Row gutter={FINANCE_READONLY_GUTTER} className="prepayment-readonly-grid">
          <FinanceReadonlyItem label="预付款单号" value={formData.prepayment_no} />
          <FinanceReadonlyItem label="剩余应核销（核销前）" value={formatFinanceAmount(remainingBefore)} />
          <FinanceReadonlyItem label="发票总额" value={formatFinanceAmount(invoiceTotal)} />
          <FinanceReadonlyItem label="本次核销金额" value={formatFinanceAmount(formData.written_amount)} />
          <FinanceReadonlyItem label="核销结论" value={resolutionLabel} />
          {Number(formData.refund_amount) > 0 && (
            <FinanceReadonlyItem label="退回预算金额" value={formatFinanceAmount(formData.refund_amount)} />
          )}
          {Number(formData.append_amount) > 0 && (
            <FinanceReadonlyItem label="追加报销金额" value={formatFinanceAmount(formData.append_amount)} />
          )}
          {formData.append_claim_no && (
            <FinanceReadonlyItem label="追加报销单号" value={formData.append_claim_no} />
          )}
        </Row>

        {allocations.length > 0 && (
          <>
            <div style={{ marginTop: 12, marginBottom: 8, fontWeight: 500 }}>预算扣减分摊</div>
            <Table
              rowKey={(row, index) => `${row.org_code}-${index}`}
              size="small"
              columns={allocationColumns}
              dataSource={allocations.map((item, index) => ({
                ...item,
                lineNo: index + 1,
                org_name: findOrgDisplayName(organizations, item.org_code, item.org_name)
              }))}
              pagination={false}
              tableLayout="fixed"
            />
          </>
        )}

        {resolution === 'refund' && refundFiles.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="prepayment-readonly-item">
              <Text type="secondary" className="prepayment-readonly-label">退回转账凭证</Text>
              <SealApprovalAttachmentsView attachments={refundFiles} title="" />
            </div>
          </div>
        )}

        {resolution === 'append_claim' && appendPreview?.lines?.length > 0 && (
          <>
            <div style={{ marginTop: 12, marginBottom: 8, fontWeight: 500 }}>追加报销明细（随核销一并提交）</div>
            <Table
              rowKey="key"
              size="small"
              columns={appendPreviewColumns}
              dataSource={buildAppendPreviewRows(appendPreview.lines)}
              pagination={false}
              tableLayout="fixed"
              scroll={{ x: 520 }}
            />
            <div style={{ marginTop: 8, color: '#666' }}>
              追加报销合计：{formatFinanceAmount(appendPreview.actual_pay_total || appendPreview.amount)}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
